import moment from "moment";

export function momentToDate(mo: moment.Moment): IDate {
  return {
    type: "date",
    value: mo.format("YYYY-MM-DD"),
  };
}

export function momentToDateTime(mo: moment.Moment): IDateTime {
  return {
    type: "datetime",
    value: mo.toISOString(),
  };
}

export function isDate(x: any): x is IDate {
  return x != null
    && x.type === "date"
    && x.value;
}

export function isDateTime(x: any): x is IDateTime {
  return x != null
    && x.type === "datetime"
    && x.value;
}

export function now(): IDateTime {
  return momentToDateTime(moment.utc());
}

export function toMoment(date: IDate | IDateTime, isUtc?: boolean): moment.Moment {
  if (isDate(date)) {
    return isUtc
      ? moment.utc(date.value, "YYYY-MM-DD", true)
      : moment(date.value, "YYYY-MM-DD", true);
  }
  else {
    return isUtc
      ? moment.utc(date.value, moment.ISO_8601, true)
      : moment(date.value, moment.ISO_8601, true);
  }
}

export function createDate(value: string): IDate {
  return {
    type: "date",
    value,
  };
}

export function createDateFromParts(dayOfMonth: number, month: number, year: number): IDate {
  return {
    type: "date",
    value: moment([year, month - 1, dayOfMonth]).format("YYYY-MM-DD"),
  };
}

export function createDateTime(value: string): IDateTime {
  return {
    type: "datetime",
    value,
  };
}

export function toDate(dateTime: IDateTime): IDate | null {
  return dateTime != null ? momentToDate(toMoment(dateTime)) : null;
}

export function toDateTime(dateTime: IDate): IDateTime | null {
  return dateTime != null ? momentToDateTime(toMoment(dateTime)) : null;
}

/**
 * Returns today's date as an IDate object, where 'today' is the current date in the UTC timezone.
 * In other timezones, this may produce a date which is not the current day locally.
 */
export function today(local = false): IDate {
  const todayMoment = local ? moment() : moment.utc();
  return momentToDate(todayMoment);
}

export function diffDays(a: IDate | IDateTime, b: IDate | IDateTime): number {
  const dateA = isDateTime(a) ? toDate(a) : a;
  const dateB = isDateTime(b) ? toDate(b) : b;
  return dateA && dateB
  ? toMoment(dateA).diff(toMoment(dateB), "days")
  : NaN;
}

export function diffHours(a: IDateTime, b: IDateTime): number {
  return toMoment(a).diff(toMoment(b), "hours");
}

export function formatDate(date: IDate, format: string): string {
  return date != null ? toMoment(date).format(format) : "";
}

export function formatDateTime(dateTime: IDateTime, format: string, isUtc?: boolean): string {
  return dateTime != null ? toMoment(dateTime, isUtc).format(format) : "";
}

export function addDays(date: IDate, days: number): IDate {
  return momentToDate(toMoment(date).add(days, "days"));
}

export function subtractDays(date: IDate, days: number): IDate {
  return momentToDate(toMoment(date).subtract(days, "days"));
}

export function addMonths(date: IDate, months: number): IDate {
  return momentToDate(toMoment(date).add(months, "months"));
}

export function isAfter(a: IDate | IDateTime, b: IDate | IDateTime): boolean {
  if (isDate(a) || isDate(b)) {
    return toMoment(a).isAfter(toMoment(b), "days");
  }

  return toMoment(a).isAfter(toMoment(b));
}

export function isBefore(a: IDate | IDateTime, b: IDate | IDateTime): boolean {
  if (isDate(a) || isDate(b)) {
    return toMoment(a).isBefore(toMoment(b), "days");
  }

  return toMoment(a).isBefore(toMoment(b));
}

export function isSame(a: IDate, b: IDate): boolean {
  return a.value === b.value;
}

export function fromNow(date: IDate | IDateTime): string {
  return toMoment(date).fromNow();
}

export function getDayOfMonth(date: IDate | IDateTime): number {
  return toMoment(date).date();
}

export function setDayOfMonth<T extends IDate | IDateTime>(date: T, dayOfMonth: number): T {
  const m = toMoment(date).date(dayOfMonth);
  return (isDate(date) ? momentToDate(m) : momentToDateTime(m)) as T;
}

export function getMonth(date: IDate | IDateTime): number {
  return toMoment(date).month() + 1;
}

export function getYear(date: IDate | IDateTime): number {
  return toMoment(date).year();
}

export function atMidnightUtc(date: IDate): IDateTime {
  return createDateTime(`${date.value}T00:00:00.000Z`);
}

export function mergeDateTime(dateTime: IDateTime, merge: IDate|ITimeOfDay) {
  const targetDT = toMoment(dateTime);
  const mergeDate = merge as IDate;
  if (mergeDate.type) {
    const d = toMoment(mergeDate);
    targetDT.date(d.date());
    targetDT.month(d.month());
    targetDT.year(d.year());
  }
  else {
    const time = merge as ITimeOfDay;
    const hour = +time.hour;
    const minute = +time.minute;
    targetDT.hour(hour);
    targetDT.minute(minute);
    targetDT.second(0);
    targetDT.millisecond(0);
  }

  return momentToDateTime(targetDT);
}

export function getTimeOfDay(date: IDateTime): ITimeOfDay {
  const d = toMoment(date);
  return { hour: d.hour(), minute: d.minute() };
}

export function createDateTimeFromUnixTimestamp(timestamp: number): IDateTime {
  return momentToDateTime(moment.unix(timestamp).utc());
}

export function isValidDateTime(datetime: IDateTime): boolean {
  if (isDateTime(datetime) && datetime.value) {
    return true;
  }

  return false;
}

/**
 * When dates are parsed in Moment they are manipulated by their time zone and daylight savings time.
 * Additionally after atMidnightUtc is used to manipulate a dateTime, and put in to a moment object,
 * timezone and DST status is lost. So if we do atMidnightUtc -> MomentObj and want to remove timezone
 * and DST we need to call moment again to do so.
 */
export function noTimeZoneAndDST(momentDate: moment.Moment): moment.Moment {
  momentDate = momentDate.add(moment().zone(), "minutes");
  momentDate = moment().isDST() ? momentDate.subtract(60, "minutes") : momentDate;
  return momentDate;
}

/**
 * Outputs relative time to now as 2y, 10m, 15d, etc
 */
export const fromNowShort = (date: IDate | IDateTime, isUtc: boolean): string => {
  const asMoment = toMoment(date, isUtc);
  /**
   * Using Maths.abs as opposed to moment.duration().abs(), .abs() is not documented in moment
   */
  const diff = moment.duration(Math.abs(asMoment.diff(moment())));

  const d = diff.asDays();
  if (d > 30) { // day precision up to 30 days
    return asMoment.format("MMM 'YY");
  }

  if (d >= 2) { // hour precision up to 48hrs
    return `${Math.floor(d).toFixed(0)}d`;
  }

  const h = diff.asHours();
  if (h >= 1) {
    return `${Math.floor(h).toFixed(0)}h`;
  }

  const min = diff.asMinutes();
  return min >= 1 ? `${Math.floor(min).toFixed(0)}m` : `< 1m`;
};
